import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { GetServerSidePropsContext } from 'next'

import runMiddleware from 'lib/middleware'
import { initializeApollo, addApolloState } from 'lib/apolloClient'
import useFieldfolioPrices from 'hooks/useFieldfolioPrices'

import BasePage from 'modules/marketplace/layout/components/BasePage'
import ButtonAnchor from 'components/ButtonAnchor'
import MarketingFaqList from 'modules/website/components/MarketingFaqList'
import { RegularContainer, SlimContainer } from 'modules/marketplace/layout/components/ResponsiveContainers'
import Section from 'modules/website/components/MarketingPageSection'
import PricingPanel from 'modules/website/components/MarketingPricingPanel'

import GET_FIELDFOLIO_RECURRING_PLAN_DETAILS from 'hooks/graphql/GetFieldfolioRecurringPlanDetails.graphql'

import styles from './PricingPage.module.css'

const PricingPage = () => {
  const { getRecurringPlanPricing, recurringTrialPeriodDays } = useFieldfolioPrices({})

  const repPrice = getRecurringPlanPricing({ component: 'reps', currency: 'AUD' })
  const catalogPrice = getRecurringPlanPricing({ component: 'catalogs', currency: 'AUD' })
  const trialPeriodLength = recurringTrialPeriodDays

  const faqs = [
    {
      q: 'Does Fieldfolio take commission on sales?',
      a: 'No. Fieldfolio Sellers only pay a monthly subscription.'
    },
    {
      q: 'Do I need to pay anything upfront to start selling on Fieldfolio?',
      a: 'No. There are no setup fees.'
    },
    {
      q: 'Is there a free trial period?',
      a: `Yes. We offer a ${trialPeriodLength} day free trial period starting when your catalog is live. You can take your time getting everything just right before the trial period begins.`
    },
    {
      q: 'Are there any extra charges if I have more Sales Reps?',
      a: `Yes. Each extra Sales Rep is an additional A$${repPrice} per month`
    },
    {
      q: 'Can I create more than one catalog?',
      a: `Yes. Each extra catalog is an additional A$${catalogPrice} per month, and our higher plans include more than one catalog.`
    },
    {
      q: 'Can Admin Staff take orders on the iPad app?',
      a: 'Yes. Fieldfolio excels at trade shows.'
    }
  ]

  return (
    <BasePage hasMarketingFooter>
      <Head>
        <title>Pricing · Fieldfolio</title>
        <meta name="description" content="Australia's best wholesale platform. Sell in the field and online." />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/pricing`} />
      </Head>

      <Section isCentered isHero>
        <RegularContainer className={styles.header}>
          <h1 className="headline">Simple, predictable pricing.</h1>
          <h1 className="subhead">No commission.</h1>
        </RegularContainer>
        <PricingPanel />
        <RegularContainer>
          <p>
            <Link href="/signup" passHref>
              <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
            </Link>
          </p>
        </RegularContainer>
      </Section>

      <Section isTinted>
        <SlimContainer>
          <h3>Frequently asked questions</h3>
          <MarketingFaqList faqs={faqs} />
          <p>
            <Link href="/signup" passHref>
              <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
            </Link>
          </p>
        </SlimContainer>
      </Section>
    </BasePage>
  )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  runMiddleware(ctx)

  const apolloClient = initializeApollo()

  await apolloClient.query({
    query: GET_FIELDFOLIO_RECURRING_PLAN_DETAILS
  })

  return addApolloState(apolloClient, { props: {} })
}

export default PricingPage
